// src/api/publicAuth.js
// Anonymous authentication/registration requests for LawAfrica Backend 08BC.
// These calls deliberately bypass the authenticated Axios interceptor so a
// previously signed-in browser session can never leak into public signup/login.

import { API_BASE_URL } from "./client";

function extractMessage(payload, fallback) {
  if (typeof payload === "string" && payload.trim()) return payload.trim();
  if (payload && typeof payload === "object") {
    if (typeof payload.message === "string" && payload.message.trim()) return payload.message.trim();
    if (typeof payload.detail === "string" && payload.detail.trim()) return payload.detail.trim();
    if (typeof payload.title === "string" && payload.title.trim()) return payload.title.trim();
  }
  return fallback;
}

async function readBody(response) {
  const text = await response.text();
  if (!text) return null;

  try {
    const parsed = JSON.parse(text);
    return parsed?.data ?? parsed;
  } catch {
    return text;
  }
}

async function request(path, options = {}) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      Accept: "application/json",
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.headers || {}),
    },
  });

  const data = await readBody(response);

  if (!response.ok) {
    const error = new Error(extractMessage(data, `Request failed (${response.status}).`));
    error.status = response.status;
    error.code = data?.code || data?.Code || null;
    error.data = data;
    throw error;
  }

  return data;
}

export function createPublicRegistrationIntent(payload) {
  return request("/registration/intent", {
    method: "POST",
    body: JSON.stringify({
      userType: "Public",
      firstName: String(payload.firstName || "").trim(),
      lastName: String(payload.lastName || "").trim(),
      email: String(payload.email || "").trim(),
      username: String(payload.username || "").trim(),
      phoneNumber: String(payload.phoneNumber || "").trim() || null,
      countryId: payload.countryId ? Number(payload.countryId) : null,
      password: payload.password || "",
    }),
  });
}

export function completePublicRegistration(intentId) {
  return request(`/registration/complete/${encodeURIComponent(intentId)}`, {
    method: "POST",
  });
}

export function getPublicRegistrationIntentStatus(intentId) {
  return request(`/registration/intent/${encodeURIComponent(intentId)}/status`);
}

export function loginUniversal(identifier, password) {
  return request("/Auth/login", {
    method: "POST",
    body: JSON.stringify({
      username: String(identifier || "").trim(),
      password: password || "",
    }),
  });
}

export function confirmRegistration(identifier, password) {
  return request("/Auth/confirm-registration", {
    method: "POST",
    body: JSON.stringify({
      username: String(identifier || "").trim(),
      password: password || "",
    }),
  });
}

export function confirmTwoFactor(identifier, code) {
  return request("/Auth/confirm-2fa", {
    method: "POST",
    body: JSON.stringify({
      username: String(identifier || "").trim(),
      code: String(code || "").trim(),
    }),
  });
}

export function requestPasswordReset(email) {
  return request("/Auth/request-password-reset", {
    method: "POST",
    body: JSON.stringify({ email: String(email || "").trim() }),
  });
}

export async function getPublicCountries() {
  const data = await request("/Country");
  const list = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];

  return list
    .map((item) => ({
      id: Number(item?.id ?? item?.Id),
      name: String(item?.name ?? item?.Name ?? "").trim(),
      isoCode: String(item?.isoCode ?? item?.IsoCode ?? "").trim(),
    }))
    .filter((item) => Number.isFinite(item.id) && item.id > 0 && item.name)
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function publicAuthErrorMessage(error, fallback = "Something went wrong. Please try again.") {
  if (typeof error?.message === "string" && error.message.trim()) return error.message.trim();
  return fallback;
}
